import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  createColumnHelper,
  flexRender,
  SortingState,
  ColumnFiltersState,
  FilterFn,
} from '@tanstack/react-table';
import { fmtCNPJ, fmtPhone, fmtDate, fmtDateTime, getTodayStr } from '../utils/formatters';
import { clientDDRsService, telecomCarriersService } from '../services/api';
import ListToolbar, { ToolbarSearch } from './shared/ListToolbar';
import CascaDeModal from './shared/CascaDeModal';
import { campo, mensagemDoErroDeCarga, opcaoAtualDispensada } from '../design';
import { SEM_BARRA, TONE_BADGE, TONE_BARRA, TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import { enderecosEmComum } from '../utils/enderecoDeAcesso';
import { corDaOperadora } from '../utils/statusColors';
import { CascaDaLista, TabelaDaLista, CabecalhoDaLista, ColunaDaLista, LinhaDaLista, CelulaId, CelulaCliente, CelulaAcoes, PaginacaoDaLista } from './shared/lista/PecasDaLista';
import { nomeDeExibicao } from '../utils/nomeDeExibicao';

interface DDRsTableProps {
  /**
   * O termo da busca global (barra do topo) ou do "ver DDRs" do cliente (15/10/2026): a tela abre já filtrada por ele.
   * Vinha para o `search` do App, que esta tela não lê (a busca dela é própria). Mesmo padrão de Faturas e Despesas.
   */
  initialSearch?: string;
  onSearchConsumed?: () => void;
  clientDDRs: any[];
  ddrStats: any;
  canEdit: boolean;
  loading: boolean;
  onEdit: (ddr: any) => void;
  onDelete: (ddr: any) => void;
  onImportDone: () => Promise<void>;
  showToast: (message: string, type: 'success' | 'error' | 'warning' | 'info') => void;
  Icon: React.FC<{ name: string; className?: string }>;
  onOpenPrestadoras?: () => void;
  /**
   * R1 a R3 (12/10/2026, medição do bloco F): o estado da carga dos DDRs (cargaDosDdrs do App). Sem ele a tela afirmava
   * "Total DDRs 0" e "Nenhum DDR encontrado" com a leitura falhando, e oferecia o Importar CSV por cima. Ausente = como
   * antes (o `loading` global), para quem monta a tabela sem o estado.
   */
  carga?: { estado: string; erro: string | null; atualizadoEm: string | null };
  /** Tentar de novo da faixa de carga. */
  onRecarregar?: () => void;
  /**
   * Os clientes, com os endereços de acesso de cada um (E1: o GET /clients traz `accessAddresses` sempre). O lote usa
   * para oferecer só os endereços comuns a todos os clientes do escopo. Ausente = o lote segue o texto de antes.
   */
  clientes?: any[];
}

/**
 * Endereço de acesso (21/10/2026, era "URL App"): o host do PABX/SBC onde o tronco do DDR termina. A base guarda o mesmo
 * host com e sem https:// e barra (https://sbc.ipcom.app.br/, sbc.ipcom.app.br/, sbc.ipcom.app.br); a tela mostra só o host.
 */
export const hostDoEndereco = (url?: string | null): string =>
  String(url || '').trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '');

/**
 * Lote com todos os campos à vista (A1 + B1, Heitor 21/10/2026, à noite). O lote campo a campo da #556 obrigava a escolher
 * cada campo e a dar valor a ele; o Heitor quer ver os cinco de uma vez e mudar só o que preencher. Então campo em branco
 * NÃO MEXE (não vai no payload; o backend só toca o que chega), e apagar não pode mais ser "deixar vazio": é o link
 * "limpar em todos" ao lado do campo, que o deixa em vermelho com "desfazer". Fornecedor não limpa: é obrigatório no cadastro.
 */
type CampoDoLote = 'carrierId' | 'hostCarrierId' | 'appUrl' | 'techPrefix' | 'channels';
export const CAMPOS_DO_LOTE: { campo: CampoDoLote; nome: string; podeLimpar: boolean }[] = [
  { campo: 'carrierId', nome: 'Fornecedor', podeLimpar: false },
  { campo: 'hostCarrierId', nome: 'Operadora real', podeLimpar: true },
  { campo: 'appUrl', nome: 'Endereço de acesso', podeLimpar: true },
  { campo: 'techPrefix', nome: 'Tech prefix', podeLimpar: true },
  { campo: 'channels', nome: 'Canais', podeLimpar: true },
];
/** O valor de um campo marcado com "limpar em todos". '' é "não alterar". */
const LIMPAR = '__limpar__';
const LOTE_VAZIO: Record<CampoDoLote, string> = { carrierId: '', hostCarrierId: '', appUrl: '', techPrefix: '', channels: '' };

// K1 (12/10/2026, medição do bloco F): a operadora era cor de ESTADO (ALGAR verde, CLARO vermelho, OI amarelo), lida
// como ok/erro. 22/10/2026 (decisão do Heitor): volta a ter cor, mas de IDENTIDADE, pelo mapa do domínio
// (COR_DA_OPERADORA em utils/statusColors), sem nenhuma cor de estado; a operadora fora do mapa fica neutra.
const carrierPillClass = (name?: string): string => corDaOperadora(name);
const carrierBarClass = (_name?: string): string => 'bg-primary';

// DDR interconectado: operadora real preenchida e diferente do fornecedor.
// Compara pelos NOMES EFETIVOS (prestadora vinculada tem prioridade sobre o
// enum/texto), senão dá falso positivo quando a prestadora está fora do enum
// (ex.: "TVN VOZ" → carrier enum "Outro" ≠ hostCarrier "TVN VOZ", mas é a mesma).
const isInterconnected = (d: any): boolean => {
  const forn = String(d.carrierPrestadora?.nome || d.carrier || '').toUpperCase().trim();
  const host = String(d.hostCarrierPrestadora?.nome || d.hostCarrier || '').toUpperCase().trim();
  return !!host && host !== forn;
};

const globalFilterFn: FilterFn<any> = (row, _columnId, filterValue) => {
  const s = (filterValue as string).toLowerCase().trim();
  if (!s) return true;
  const d = row.original;
  const sDigits = s.replace(/\D/g, '');
  return (d.phoneNumber || '').includes(filterValue) ||
    (d.client?.name || '').toLowerCase().includes(s) ||
    (d.client?.tradeName || '').toLowerCase().includes(s) ||
    (d.client?.companyName || '').toLowerCase().includes(s) ||
    (d.city || '').toLowerCase().includes(s) ||
    (d.location || '').toLowerCase().includes(s) ||
    (d.endClientName || '').toLowerCase().includes(s) ||
    (d.techPrefix || '').toLowerCase().includes(s) ||
    (d.endClientCnpj || '').replace(/\D/g, '').includes(sDigits.length >= 3 ? sDigits : '___') ||
    (sDigits.length >= 3 && (d.client?.cnpj || '').replace(/\D/g, '').includes(sDigits));
};

const carrierFilterFn: FilterFn<any> = (row, _columnId, filterValue) => {
  if (!filterValue || filterValue === 'Todos') return true;
  const name = row.original.carrierPrestadora?.nome || row.original.carrier;
  return name === filterValue;
};

const hostCarrierFilterFn: FilterFn<any> = (row, _columnId, filterValue) => {
  if (!filterValue || filterValue === 'Todos') return true;
  const host = String(row.original.hostCarrierPrestadora?.nome || row.original.hostCarrier || '').toUpperCase();
  if (filterValue === 'Não verificado') return !host;
  return host === String(filterValue).toUpperCase();
};

const stateFilterFn: FilterFn<any> = (row, _columnId, filterValue) => {
  if (!filterValue || filterValue === 'Todos') return true;
  const d = row.original;
  if (filterValue === 'CNG') {
    return d.city === 'CNG 0800' || !d.state;
  }
  return d.state === filterValue;
};

const columnHelper = createColumnHelper<any>();


const DDRsTable: React.FC<DDRsTableProps> = ({
  clientDDRs,
  ddrStats,
  canEdit,
  loading,
  onEdit,
  onDelete,
  onImportDone,
  showToast,
  Icon,
  initialSearch,
  onSearchConsumed,
  onOpenPrestadoras,
  carga,
  onRecarregar,
  clientes,
}) => {
  // R1: sem nenhuma leitura, os totais e a lista não existem (nunca "0" e "Nenhum DDR encontrado"). R3: o "Carregando…"
  // só na primeira carga; depois, a tabela fica durante a recarga.
  const lido = carga ? carga.atualizadoEm !== null : true;
  const primeiraCarga = carga ? carga.atualizadoEm === null && carga.estado === 'carregando' : loading;
  const [erroPrestadoras, setErroPrestadoras] = useState<string | null>(null);
  // R6: o que o servidor recusou na última importação (linha a linha), até a pessoa fechar.
  const [recusasDaImportacao, setRecusasDaImportacao] = useState<string[] | null>(null);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'phoneNumber', desc: false }]);
  const [globalFilter, setGlobalFilter] = useState(initialSearch || '');
  useEffect(() => {
    if (initialSearch) { setGlobalFilter(initialSearch); onSearchConsumed?.(); }
  }, [initialSearch, onSearchConsumed]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [carrierFilter, setCarrierFilter] = useState('Todos');
  const [hostFilter, setHostFilter] = useState('Todos');
  const [interconnectedOnly, setInterconnectedOnly] = useState(false);
  const [stateFilter, setStateFilter] = useState('Todos');
  const [statusFilter, setStatusFilter] = useState<'Ativos' | 'Inativos' | 'Todos'>('Ativos');
  const [carrierView, setCarrierView] = useState<'fornecedor' | 'real'>('fornecedor');
  const [importing, setImporting] = useState(false);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [showBulk, setShowBulk] = useState(false);
  // L1: os campos que a pessoa escolheu mudar, na ordem em que escolheu, e o valor de cada um ('' = ainda não escolhido).
  // A1: o valor de cada campo do lote ('' = não alterar; LIMPAR = apagar em todos).
  const [bulkValores, setBulkValores] = useState<Record<CampoDoLote, string>>(LOTE_VAZIO);
  const [prestadoras, setPrestadoras] = useState<any[]>([]);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const totalDDRs = clientDDRs.length;
  // Contabilidade dos cards é SÓ de linhas ATIVAS (inativas ficam fora do cálculo)
  const activeList = useMemo(() => clientDDRs.filter((d: any) => d.status === 'Ativo'), [clientDDRs]);
  const activeDDRs = activeList.length;

  // Opções dos FILTROS usam todos os DDRs (a tabela mostra todos os status)
  const availableStates = useMemo(() =>
    (Array.from(new Set(clientDDRs.map((d: any) => d.state).filter(Boolean))) as string[]).sort(),
    [clientDDRs]
  );

  // Fornecedores presentes nos dados — usa o nome da prestadora linkada (carrierPrestadora)
  // com fallback no enum legado, pra refletir renomeações (ex.: TIP → TVN VOZ).
  const availableCarriers = useMemo(() =>
    (Array.from(new Set(clientDDRs.map((d: any) => d.carrierPrestadora?.nome || d.carrier).filter(Boolean))) as string[]).sort(),
    [clientDDRs]
  );

  const availableHostCarriers = useMemo(() =>
    (Array.from(new Set(clientDDRs.map((d: any) => d.hostCarrierPrestadora?.nome || d.hostCarrier).filter(Boolean))) as string[]).sort(),
    [clientDDRs]
  );

  const interconnectedCount = useMemo(() => activeList.filter(isInterconnected).length, [activeList]);

  const ddrsByState = useMemo(() =>
    Array.from(
      activeList.reduce((acc: Map<string, number>, d: any) => {
        const key = d.state || (d.city === 'CNG 0800' ? 'CNG' : 'N/D');
        acc.set(key, (acc.get(key) || 0) + 1);
        return acc;
      }, new Map())
    ).map(([state, count]) => ({ state, count })).sort((a: any, b: any) => b.count - a.count),
    [activeList]
  );

  const ddrsByClient = useMemo(() =>
    Array.from(
      activeList.reduce((acc: Map<number, { name: string; count: number }>, d: any) => {
        const clientId = d.clientId;
        const clientName = d.client?.name || d.client?.tradeName || d.client?.companyName || 'Sem cliente';
        if (!acc.has(clientId)) {
          acc.set(clientId, { name: clientName, count: 0 });
        }
        acc.get(clientId)!.count++;
        return acc;
      }, new Map())
    ).map(([id, data]) => ({ id, ...data })).sort((a: any, b: any) => b.count - a.count),
    [activeList]
  );

  const carrierCardData = useMemo(() => {
    const acc = new Map<string, number>();
    for (const d of activeList) {
      // Usa o NOME EFETIVO (prestadora vinculada tem prioridade sobre o enum
      // legado), igual ao resto da tabela/filtros — senão TVN VOZ aparecia como
      // "TIP"/"Outro" (enum antigo) e não batia com o total (bug 17/07/2026).
      const key = carrierView === 'real'
        ? (d.hostCarrierPrestadora?.nome || d.hostCarrier || 'Não verificado')
        : (d.carrierPrestadora?.nome || d.carrier || '');
      if (!key) continue;
      acc.set(key, (acc.get(key) || 0) + 1);
    }
    return Array.from(acc).map(([name, count]) => ({ name, count })).sort((a: any, b: any) => b.count - a.count);
  }, [activeList, carrierView]);
  const maxCarrierCard = useMemo(() => Math.max(...carrierCardData.map((c: any) => c.count), 1), [carrierCardData]);
  const maxByState = useMemo(() => Math.max(...ddrsByState.slice(0, 10).map((s: any) => s.count), 1), [ddrsByState]);

  const columns = useMemo(() => [
    columnHelper.display({
      id: 'select',
      header: ({ table }) => (
        <input
          type='checkbox'
          checked={table.getIsAllRowsSelected()}
          ref={(el) => { if (el) el.indeterminate = !table.getIsAllRowsSelected() && table.getIsSomeRowsSelected(); }}
          onChange={table.getToggleAllRowsSelectedHandler()}
          className='w-4 h-4 accent-primary cursor-pointer'
          title='Selecionar todos os DDRs do filtro atual'
        />
      ),
      // C1: estado é peso + ícone + barra. Portabilidade (ação pendente) leva a barra âmbar de 3px nesta primeira célula.
      cell: ({ row }) => (
        <td className={`px-2 py-3 border-l-[3px] ${row.original.status === 'Portabilidade' ? TONE_BARRA.warning : SEM_BARRA}`} data-barra-do-estado>
          <input
            type='checkbox'
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            className='w-4 h-4 accent-primary cursor-pointer'
          />
        </td>
      ),
      enableSorting: false,
      size: 40,
    }),
    columnHelper.accessor('id', {
      id: 'id',
      header: 'ID',
      // Fase 2 do padrão das listas (22/10/2026): as células comuns são as peças de shared/lista (guia §3c). A barra do
      // estado continua na célula de seleção, que é a primeira desta tela.
      cell: ({ row }) => <CelulaId id={row.original.id} />,
      size: 60,
    }),
    columnHelper.accessor('phoneNumber', {
      id: 'phoneNumber',
      header: 'Número',
      cell: ({ row }) => (
        <td className='px-4 py-3 whitespace-nowrap'>
          <span className='font-mono text-sm text-white'>{fmtPhone(row.original.phoneNumber)}</span>
          {row.original.status === 'Portabilidade' ? (
            <p className='mt-0.5' data-status-do-ddr='Portabilidade'>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${TONE_BADGE.warning}`}><span aria-hidden='true'>⇄ </span><span>Portabilidade</span></span>
            </p>
          ) : row.original.status && row.original.status !== 'Ativo' ? (
            <p className='mt-0.5 text-[11px] text-slate-400' data-status-do-ddr={row.original.status}><span aria-hidden='true'>◌ </span><span>{row.original.status}</span></p>
          ) : null}
        </td>
      ),
    }),
    columnHelper.accessor((row) => `${row.client?.name || row.client?.tradeName || row.client?.companyName || ''} ${row.client?.cnpj || ''}`, {
      id: 'clientName',
      header: 'Cliente',
      cell: ({ row }) => {
        const d = row.original;
        const c: any = d.client;
        const nome = c?.name || c?.tradeName || c?.companyName || '-';
        // A sub-linha não repete o nome: cliente só com nome fantasia mostrava o mesmo texto duas vezes.
        const razao = [c?.tradeName, c?.companyName].find((x) => x && x !== nome) || null;
        return (
          // C1 (22/10/2026, captura do Heitor): uma sub-linha só, razão · CNPJ · cidade do cliente, como em Clientes. A fase 2
          // tinha deixado esta célula à mão, com o CNPJ numa TERCEIRA linha; a regra cnpj-em-linha-propria passou a pegar.
          <CelulaCliente
            nome={nome}
            razao={razao}
            cnpj={c?.cnpj}
            cidade={c?.city ? `${c.city}${c.state ? `/${c.state}` : ''}` : null}
            extra={(d.endClientName || d.endClientCnpj) ? (
              // Sem nome, mostra o CNPJ \u2014 sen\u00e3o o v\u00ednculo com o cliente final fica invis\u00edvel na lista
              <p className='text-xs text-slate-400 truncate' title={`Cliente final: ${d.endClientName || 'sem nome'}${d.endClientCnpj ? ` (${fmtCNPJ(d.endClientCnpj)})` : ''}`}>
                {'\u2192'} {d.endClientName || fmtCNPJ(d.endClientCnpj)}
              </p>
            ) : undefined}
          />
        );
      },
    }),
    columnHelper.accessor((row) => row.city || row.location || '', {
      id: 'city',
      header: 'Cidade/UF',
      cell: ({ row }) => {
        const d = row.original;
        return (
          <td className='px-4 py-3'>
            <p className='text-sm text-slate-300 truncate'>{d.city && d.state ? `${d.city} - ${d.state}` : d.location || '-'}</p>
            {(d.areaLocal || d.cnl) && (
              <p className='text-xs text-slate-500 truncate' title={`${d.areaLocal || ''}${d.cnl ? ` \u00b7 CNL ${d.cnl}` : ''}${d.cnlLocalidade ? ` (${d.cnlLocalidade})` : ''}`}>
                {d.areaLocal || ''}{d.areaLocal && d.cnl ? ' \u00b7 ' : ''}{d.cnl ? `CNL ${d.cnl}` : ''}
              </p>
            )}
          </td>
        );
      },
    }),
    columnHelper.accessor('carrier', {
      id: 'carrier',
      header: 'Fornecedor',
      cell: ({ row }) => {
        const d = row.original;
        const name = d.carrierPrestadora?.nome || d.carrier;
        return (
          <td className='px-4 py-3 text-center'>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${carrierPillClass(name)}`}>{name}</span>
          </td>
        );
      },
      filterFn: carrierFilterFn,
    }),
    columnHelper.accessor('hostCarrier', {
      id: 'hostCarrier',
      header: 'Operadora Real',
      cell: ({ row }) => {
        const d = row.original;
        const hostName = d.hostCarrierPrestadora?.nome || d.hostCarrier;
        const carrierName = d.carrierPrestadora?.nome || d.carrier;
        if (!hostName) {
          return <td className='px-4 py-3 text-center'><span className='text-xs text-slate-600'>—</span></td>;
        }
        const interconnected = isInterconnected(d);
        const title = interconnected
          ? `Fornecido por ${carrierName} · Hospedado na ${hostName}${d.hostCarrierCheckedAt ? ` · verificado em ${fmtDate(d.hostCarrierCheckedAt)}` : ''}`
          : `Operadora real: ${hostName}${d.hostCarrierCheckedAt ? ` · verificado em ${fmtDate(d.hostCarrierCheckedAt)}` : ''}`;
        // A DIVERGÊNCIA (22/10/2026, decisão do Heitor): operadora real ≠ fornecedor é o que pede atenção, e tem de chamar
        // mais que a cor da operadora: anel âmbar no chip e o "hospedado" com ícone, em âmbar e peso. Igual ao fornecedor,
        // a célula fica discreta: só o nome, cinza, sem chip (repetiria a coluna ao lado).
        if (!interconnected) {
          return (
            <td className='px-4 py-3 text-center' title={title} data-operadora-real='igual'>
              <span className='text-xs text-slate-500'>{hostName}</span>
            </td>
          );
        }
        return (
          <td className='px-4 py-3 text-center' title={title} data-operadora-real='divergente'>
            <div className='flex flex-col items-center gap-1'>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ring-2 ring-amber-400/70 ${carrierPillClass(hostName)}`}>{hostName}</span>
              {/* O ícone é o ⇄ (hospedado/interconectado). O Icon que a tabela recebe é o do App, que não tem "alerta". */}
              <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${TONE_TEXT.warning}`}>
                <span aria-hidden='true'>⇄</span> hospedado
              </span>
            </div>
          </td>
        );
      },
      filterFn: hostCarrierFilterFn,
    }),
    // R1 (21/10/2026): "URL App" é o ENDEREÇO DE ACESSO do PABX/SBC do cliente; mostra só o host, ainda como link. O Tech
    // prefix, que é parâmetro do mesmo tronco, mora embaixo dele (antes ficava embaixo da Tecnologia, que saiu: T1).
    // O fallback d.client?.appUrl saiu: clients não tem essa coluna, ele nunca disparava.
    // E1 (21/10/2026): o endereço vinculado (accessAddress) vence o texto antigo, que o backend mantém por um ciclo.
    columnHelper.accessor((row) => row.accessAddress?.host || hostDoEndereco(row.appUrl), {
      id: 'accessAddress',
      header: 'Endereço de acesso',
      cell: ({ row }) => {
        const d = row.original;
        const host = d.accessAddress?.host || hostDoEndereco(d.appUrl);
        return (
          <td className='px-4 py-3' data-endereco-de-acesso>
            {host ? (
              <a
                href={`https://${host}`}
                target='_blank'
                rel='noopener noreferrer'
                onClick={(e) => e.stopPropagation()}
                className='block text-xs text-slate-200 underline decoration-slate-600 underline-offset-2 hover:text-white truncate'
                title={host}
              >
                {host}
              </a>
            ) : (
              <span className='text-xs text-slate-500'>sem endereço</span>
            )}
            {d.techPrefix && <p className='text-[10px] font-mono text-slate-500 mt-0.5'>tp {d.techPrefix}</p>}
          </td>
        );
      },
    }),
    // K1: Canais em coluna própria (dividia a célula com a URL). Vazio é "—"; zero gravado aparece como 0.
    columnHelper.accessor((row) => (row.channels == null || row.channels === '' ? -1 : Number(row.channels)), {
      id: 'channels',
      header: 'Canais',
      cell: ({ row }) => {
        const c = row.original.channels;
        return (
          <td className='px-4 py-3 text-right tabular-nums text-sm' data-canais>
            {c == null || c === '' ? <span className='text-slate-500'>—</span> : <span className='text-slate-300'>{c}</span>}
          </td>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Ações',
      cell: ({ row }) => {
        const d = row.original;
        // D2 (o padrão de toda lista): editar + ⋯; Excluir mora no ⋯, em perigo, e confirma (o App pergunta antes). A
        // âncora do menu é fixa (CelulaAcoes): a tabela rola e cortaria o menu.
        return (
          <CelulaAcoes
            rotuloDoMenu={`Mais ações do DDR ${d.phoneNumber}`}
            onEditar={() => onEdit(d)}
            Icon={Icon}
            itens={[{ label: 'Excluir', onClick: () => onDelete(d), danger: true }]}
          />
        );
      },
    }),
  ], [onEdit, onDelete, Icon]);

  // State + "interconectados" filters são pré-filtros manuais (propriedades virtuais/derivadas)
  const stateFilteredData = useMemo(() => {
    let rows = clientDDRs;
    if (statusFilter === 'Ativos') rows = rows.filter((d: any) => d.status === 'Ativo');
    else if (statusFilter === 'Inativos') rows = rows.filter((d: any) => d.status !== 'Ativo');
    if (stateFilter === 'CNG') rows = rows.filter((d: any) => d.city === 'CNG 0800' || !d.state);
    else if (stateFilter !== 'Todos') rows = rows.filter((d: any) => d.state === stateFilter);
    if (interconnectedOnly) rows = rows.filter(isInterconnected);
    return rows;
  }, [clientDDRs, statusFilter, stateFilter, interconnectedOnly]);

  const columnVisibility = useMemo(() => ({
    actions: canEdit,
  }), [canEdit]);

  const table = useReactTable({
    data: stateFilteredData,
    columns,
    state: {
      sorting,
      globalFilter,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    getRowId: (row) => String(row.id),
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    globalFilterFn,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: { pageSize: 50 },
    },
  });

  const handleCarrierFilter = (carrier: string) => {
    setCarrierFilter(carrier);
    const col = table.getColumn('carrier');
    if (col) col.setFilterValue(carrier === 'Todos' ? undefined : carrier);
  };

  const handleHostFilter = (host: string) => {
    setHostFilter(host);
    const col = table.getColumn('hostCarrier');
    if (col) col.setFilterValue(host === 'Todos' ? undefined : host);
  };

  const handleStateFilter = (uf: string) => {
    setStateFilter(uf);
    table.setPageIndex(0);
  };

  const handleExportCSV = useCallback(() => {
    const allFilteredRows = table.getFilteredRowModel().rows;
    const csvHeader = 'Número,Cliente,Nome Exibição,CNPJ,Cidade,UF,Área Local,Cód Área Local,CNL,CNL Localidade,CNL Área Tarifação,Fornecedor,Operadora Real,Verificado em,Tech Prefix,Canais,Status,Contrato,Endereço de acesso\n';
    const csvData = allFilteredRows.map((row) => {
      const d = row.original;
      return [
        d.phoneNumber,
        nomeDeExibicao(d.client),
        d.client?.name || '',
        d.client?.cnpj || '',
        d.city || '',
        d.state || '',
        d.areaLocal || '',
        d.areaLocalCodigo || '',
        d.cnl || '',
        d.cnlLocalidade || '',
        d.cnlAreaTarifacao || '',
        d.carrierPrestadora?.nome || d.carrier || '',
        d.hostCarrierPrestadora?.nome || d.hostCarrier || '',
        d.hostCarrierCheckedAt ? fmtDate(d.hostCarrierCheckedAt) : '',
        d.techPrefix || '',
        d.channels || '',
        d.status || '',
        d.contractNumber || '',
        d.accessAddress?.host || hostDoEndereco(d.appUrl)
      ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',');
    }).join('\n');

    const blob = new Blob([csvHeader + csvData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ddrs_${getTodayStr()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showToast(`${allFilteredRows.length} registros exportados`, 'success');
  }, [table, showToast]);

  const importCSVFile = useCallback(async (file: File | undefined) => {
    if (!file) return;

    const reader = new FileReader();
    // R7: o arquivo que não se deixa ler não ficava em silêncio só porque o onload nunca vinha.
    reader.onerror = () => showToast(`Não consegui ler o arquivo ${file.name}: nada foi importado.`, 'error');
    reader.onload = async (event) => {
      try {
        const text = event.target?.result as string;
        const lines = text.split('\n').filter(l => l.trim());
        const _header = lines[0].split(','); // eslint-disable-line @typescript-eslint/no-unused-vars

        const data = lines.slice(1).map(line => {
          const values = line.split(',');
          return {
            carrier: values[0]?.trim(),
            // T1 (21/10/2026): a coluna 2 do arquivo (tecnologia) é ignorada. Todas as contas são trunk, e o backend ainda
            // grava SIP quando a tecnologia vem vazia; mandar Trunk evita que a importação crie (ou devolva) SIP.
            technology: 'Trunk',
            phoneNumber: values[2]?.trim(),
            contractNumber: values[3]?.trim(),
            clientCnpj: values[8]?.trim(),
            location: values[5]?.trim(),
            appUrl: values[6]?.trim(),
            techPrefix: values[9]?.trim(),
            channels: values[10] ? parseInt(values[10]) : undefined,
            hostCarrier: values[11]?.trim(), // operadora real (hospedagem) — coluna 12
          };
        }).filter(d => d.phoneNumber);

        setImporting(true);
        setRecusasDaImportacao(null);
        const result = await clientDDRsService.import(data);
        // R6: as recusas por linha iam só para o console, e o toast dizia "concluída" com linha recusada.
        const recusas: string[] = Array.isArray(result.errors) ? result.errors : [];
        if (recusas.length > 0) {
          setRecusasDaImportacao(recusas);
          showToast(`Importação com recusas: ${result.imported} novos, ${result.updated} atualizados, ${recusas.length} linha(s) recusada(s). A lista está acima da tabela.`, 'warning');
        } else {
          showToast(`Importação concluída: ${result.imported} novos, ${result.updated} atualizados`, 'success');
        }
        await onImportDone();
      } catch (error: any) {
        showToast('Erro ao importar: ' + error.message, 'error');
      } finally {
        setImporting(false);
      }
    };
    reader.readAsText(file);
  }, [showToast, onImportDone]);

  const handleImportCSV = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    importCSVFile(e.target.files?.[0]);
    e.target.value = '';
  }, [importCSVFile]);

  const visibleColumnCount = table.getVisibleLeafColumns().length;
  const selectedCount = table.getSelectedRowModel().rows.length;

  const openBulk = async () => {
    setShowBulk(true);
    if (prestadoras.length === 0) {
      try {
        const list = await telecomCarriersService.getAll();
        setPrestadoras(Array.isArray(list) ? list : (list?.data || []));
        setErroPrestadoras(null);
      } catch (e) {
        // R4: era silencioso, e o select vazio parecia "nenhuma prestadora cadastrada". Agora diz; abrir de novo tenta de novo.
        setErroPrestadoras(mensagemDoErroDeCarga(e));
      }
    }
  };

  /**
   * Exclusão em lote. Diferente do "Alterar em lote", NÃO cai no filtro atual
   * quando nada está marcado: apagar tudo que a busca por acaso retornou é o
   * tipo de engano que não tem desfazer. Aqui exige marcação explícita.
   */
  const applyBulkDelete = async () => {
    const ids = table.getSelectedRowModel().rows.map((r: any) => r.original.id);
    if (ids.length === 0) { showToast('Marque os DDRs que quer excluir', 'warning'); return; }
    setBulkDeleting(true);
    try {
      const res = await clientDDRsService.bulkDelete(ids);
      showToast(`${res?.affected ?? ids.length} DDR(s) excluídos`, 'success');
      setShowBulkDelete(false);
      setRowSelection({});
      await onImportDone();
    } catch {
      showToast('Não foi possível excluir os DDRs', 'error');
    } finally {
      setBulkDeleting(false);
    }
  };

  const linhasDoEscopo = selectedCount > 0 ? table.getSelectedRowModel().rows : table.getFilteredRowModel().rows;
  /** Os campos que vão mudar, na ordem da tela: o que tem valor ou foi marcado para limpar. Branco (ou só espaço) não mexe. */
  const camposAlterados = CAMPOS_DO_LOTE.filter((c) => bulkValores[c.campo].trim() !== '');
  /**
   * E1 no lote: se algum cliente do escopo já tem endereço cadastrado, o campo vira a lista dos endereços COMUNS a todos
   * os clientes do escopo (o backend recusa endereço que não é do cliente do DDR). Sem nenhum cliente com endereço (as
   * tabelas nasceram vazias), segue o texto de antes. O valor da lista vai como "id:<n>".
   */
  const listasDoEscopo = Array.from(new Set(linhasDoEscopo.map((r: any) => r.original.clientId))).map((id) => {
    const l = (clientes || []).find((c: any) => c.id === id)?.accessAddresses;
    return Array.isArray(l) ? (l as Array<{ id: number; host: string }>) : [];
  });
  const loteComListaDeEndereco = listasDoEscopo.some((l) => l.length > 0);
  const enderecosComuns = loteComListaDeEndereco ? enderecosEmComum(listasDoEscopo) : [];
  const hostDoId = (v: string) => enderecosComuns.find((e) => `id:${e.id}` === v)?.host || v;
  /** Como o valor escolhido se lê na frase do resumo ("TVN VOZ", "sbc.ipcom.app.br"). */
  const valorLegivel = (campo: CampoDoLote, valor: string) => {
    if (campo === 'carrierId' || campo === 'hostCarrierId') return prestadoras.find((p: any) => String(p.id) === valor)?.nome || valor;
    if (campo === 'appUrl') return valor.startsWith('id:') ? hostDoId(valor) : hostDoEndereco(valor);
    return valor.trim();
  };

  const applyBulk = async () => {
    const ids = linhasDoEscopo.map((r: any) => r.original.id);
    if (ids.length === 0) { showToast('Nenhum DDR no escopo selecionado', 'warning'); return; }
    if (camposAlterados.length === 0) { showToast('Preencha ao menos um campo para alterar', 'warning'); return; }
    // Só o campo alterado entra no payload: o que não chega o backend não toca. null apaga.
    const payload: { carrierId?: number | null; hostCarrierId?: number | null; techPrefix?: string | null; appUrl?: string | null; accessAddressId?: number | null; channels?: number | null } = {};
    for (const { campo: c } of camposAlterados) {
      const v = bulkValores[c];
      const limpar = v === LIMPAR;
      if (c === 'carrierId') payload.carrierId = Number(v);
      if (c === 'hostCarrierId') payload.hostCarrierId = limpar ? null : Number(v);
      if (c === 'techPrefix') payload.techPrefix = limpar ? null : v.trim();
      if (c === 'appUrl') {
        // Limpar apaga os dois (o vínculo e o texto); da lista, grava o vínculo e repete o host no texto.
        if (limpar) { payload.accessAddressId = null; payload.appUrl = null; }
        else if (v.startsWith('id:')) { payload.accessAddressId = Number(v.slice(3)); payload.appUrl = hostDoId(v); }
        else payload.appUrl = hostDoEndereco(v);
      }
      if (c === 'channels') payload.channels = limpar ? null : (parseInt(v, 10) || 0);
    }
    setBulkSaving(true);
    try {
      const res = await clientDDRsService.bulkUpdateCarriers(ids, payload);
      showToast(`${res.affected} DDR(s) atualizados`, 'success');
      setShowBulk(false);
      setRowSelection({});
      setBulkValores(LOTE_VAZIO);
      await onImportDone();
    } catch {
      showToast('Erro ao atualizar em lote', 'error');
    } finally {
      setBulkSaving(false);
    }
  };
  const filteredRowCount = table.getFilteredRowModel().rows.length;
  const isFiltered = filteredRowCount !== clientDDRs.length;


  const faixa = `px-4 py-3 rounded-xl border text-sm flex items-center justify-between gap-3 ${TONE_SURFACE.warning} ${TONE_BORDER.warning} ${TONE_TEXT.warning}`;
  return (
    <div className='space-y-4'>
      {carga?.estado === 'erro' && (
        <div role='alert' className={faixa} data-ddrs-faixa={lido ? 'desatualizado' : 'sem-leitura'}>
          <span>
            {lido && carga.atualizadoEm
              ? `Não consegui atualizar os DDRs (${carga.erro}). Mostrando os lidos em ${fmtDateTime(carga.atualizadoEm)}.`
              : `Não consegui ler os DDRs (${carga.erro}). Sem leitura, os totais e a lista não aparecem, e o Importar CSV espera.`}
          </span>
          {onRecarregar && <button type='button' onClick={onRecarregar} className='shrink-0 underline font-medium'>Tentar de novo</button>}
        </div>
      )}
      {recusasDaImportacao && (
        <div role='alert' className={`px-4 py-3 rounded-xl border text-sm ${TONE_SURFACE.warning} ${TONE_BORDER.warning} ${TONE_TEXT.warning}`} data-ddrs-recusas>
          <div className='flex items-center justify-between gap-3'>
            <span className='font-semibold'>A última importação recusou {recusasDaImportacao.length} linha(s):</span>
            <button type='button' onClick={() => setRecusasDaImportacao(null)} className='shrink-0 underline'>Fechar</button>
          </div>
          <ul className='mt-2 space-y-0.5 max-h-40 overflow-y-auto'>
            {recusasDaImportacao.map((r, i) => <li key={i}>• {r}</li>)}
          </ul>
        </div>
      )}
      {/* Dashboard Cards */}
      {lido && (
      <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
        {/* Card Total + Por Operadora */}
        <div className='bg-central-cartao rounded-central p-4 border border-central-borda'>
          <div className='flex items-center justify-between mb-4'>
            <div>
              <p className='text-sm text-slate-400'>Total DDRs</p>
              <p className='text-2xl font-bold text-white'>
                {isFiltered ? <>{filteredRowCount.toLocaleString()} <span className='text-sm text-slate-500 font-normal'>/ {totalDDRs.toLocaleString()}</span></> : totalDDRs.toLocaleString()}
              </p>
            </div>
            <div className='text-right'>
              <p className='text-slate-300 text-sm font-semibold'>{activeDDRs.toLocaleString()} ativos</p>
              <p className='text-slate-500 text-xs'>{totalDDRs > 0 ? ((activeDDRs / totalDDRs) * 100).toFixed(1) : 0}%</p>
            </div>
          </div>
          <div className='flex items-center justify-between mb-2'>
            <p className='text-xs text-slate-500 font-medium'>{carrierView === 'real' ? 'Por Operadora Real' : 'Por Fornecedor'} <span className='text-slate-600'>(ativos)</span></p>
            <div className='flex bg-slate-800 rounded-lg p-0.5'>
              <button
                onClick={() => setCarrierView('fornecedor')}
                className={`px-2 py-0.5 rounded-md text-[10px] font-medium transition-colors ${carrierView === 'fornecedor' ? 'bg-slate-600 text-white font-semibold' : 'text-slate-400 hover:text-slate-200'}`}
              >Fornecedor</button>
              <button
                onClick={() => setCarrierView('real')}
                className={`px-2 py-0.5 rounded-md text-[10px] font-medium transition-colors ${carrierView === 'real' ? 'bg-slate-600 text-white font-semibold' : 'text-slate-400 hover:text-slate-200'}`}
              >Op. Real</button>
            </div>
          </div>
          <div className='space-y-2'>
            {carrierCardData.map((c: any) => (
              <div key={c.name} className='flex items-center gap-2'>
                <span className='text-xs text-slate-400 w-20 truncate' title={c.name}>{c.name}</span>
                <div className='flex-1 h-4 bg-slate-800 rounded-full overflow-hidden'>
                  <div
                    className={`h-full rounded-full ${carrierBarClass(c.name)}`}
                    style={{ width: `${(c.count / maxCarrierCard) * 100}%` }}
                  />
                </div>
                <span className='text-xs text-white font-medium w-12 text-right'>{c.count}</span>
              </div>
            ))}
          </div>
          {interconnectedCount > 0 && (
            <button
              onClick={() => { setInterconnectedOnly(v => !v); table.setPageIndex(0); }}
              className={`mt-3 w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors border border-l-[3px] ${interconnectedOnly ? 'bg-central-elevado text-white font-semibold border-central-borda border-l-primary' : 'bg-central-fundo text-slate-400 hover:bg-central-elevado border-central-borda border-l-transparent'}`}
              title='Fornecedor diferente da operadora real (numeração hospedada/interconectada) — só linhas ativas'
            >
              <span>⇄ Interconectados (hospedados)</span>
              <span className='font-bold'>{interconnectedCount.toLocaleString()}</span>
            </button>
          )}
        </div>

        {/* Card Por Estado (Top 10) */}
        <div className='bg-central-cartao rounded-central p-4 border border-central-borda'>
          <p className='text-xs text-slate-500 mb-3 font-medium uppercase'>Por Estado · Ativos (Top 10)</p>
          <div className='space-y-1.5'>
            {ddrsByState.slice(0, 10).map((s: any) => (
              <div key={s.state} className='flex items-center gap-2'>
                <span className='text-xs text-slate-400 w-8'>{s.state}</span>
                <div className='flex-1 h-3 bg-slate-800 rounded-full overflow-hidden'>
                  <div
                    className='h-full rounded-full bg-primary'
                    style={{ width: `${(s.count / maxByState) * 100}%` }}
                  />
                </div>
                <span className='text-xs text-slate-300 w-12 text-right'>{s.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Card Top Clientes */}
        <div className='bg-central-cartao rounded-central p-4 border border-central-borda'>
          <p className='text-xs text-slate-500 mb-3 font-medium uppercase'>Top 10 Clientes · Ativos</p>
          <div className='space-y-1.5'>
            {ddrsByClient.slice(0, 10).map((c: any, idx: number) => (
              <div key={c.id} className='flex items-center gap-2'>
                <span className={`text-xs font-bold w-5 ${idx < 3 ? 'text-white' : 'text-slate-500'}`}>{idx + 1}</span>
                <span className='text-xs text-slate-300 flex-1 truncate'>{c.name}</span>
                <span className='text-xs text-white font-medium'>{c.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      )}

      {/* Barra de Busca e Filtros */}
      <ListToolbar
        search={<ToolbarSearch value={globalFilter} onChange={v => { setGlobalFilter(v); table.setPageIndex(0); }} placeholder='Buscar por número, DDD, cliente, cidade, tech prefix...' />}
        actions={<>
          <button onClick={handleExportCSV} className='flex items-center gap-2 px-4 py-2.5 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 font-medium whitespace-nowrap text-sm'>
            <Icon name='download' className='w-4 h-4' />
            Exportar CSV ({filteredRowCount})
          </button>
          {/* Importar CSV é ESCRITA (cria/atualiza DDR em lote): mesmo gate dos
              demais. Exportar continua para todos — leitura do que já se vê. */}
          {canEdit && <label
            className={`flex items-center gap-2 px-4 py-2.5 bg-slate-700 text-slate-300 rounded-lg font-medium whitespace-nowrap text-sm ${lido ? 'hover:bg-slate-600 cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
            title={lido ? undefined : 'Disponível depois de ler os DDRs: importar sobre uma lista não lida não deixa conferir o resultado'}
            onDragOver={(e) => { e.preventDefault(); }}
            onDrop={(e) => {
              e.preventDefault();
              if (lido && !importing && e.dataTransfer?.files?.length) importCSVFile(e.dataTransfer.files[0]);
            }}
          >
            <Icon name='upload' className='w-4 h-4' />
            {importing ? 'Importando...' : 'Importar CSV'}
            <input type='file' accept='.csv' onChange={handleImportCSV} className='hidden' disabled={importing || !lido} />
          </label>}
          {canEdit && <button onClick={openBulk} className='flex items-center gap-2 px-4 py-2.5 bg-slate-700 text-slate-200 rounded-lg hover:bg-slate-600 border border-slate-600 font-medium whitespace-nowrap text-sm' title='Alterar vários DDRs de uma vez: fornecedor, operadora real, endereço de acesso, tech prefix ou canais (linhas marcadas ou todo o filtro atual)'><Icon name='edit' className='w-4 h-4' />Alterar em lote{selectedCount > 0 && <span className='ml-1 px-1.5 py-0.5 rounded-full bg-slate-500 text-white text-[10px] font-semibold'>{selectedCount}</span>}</button>}
          {canEdit && selectedCount > 0 && (<button onClick={() => setShowBulkDelete(true)} className='flex items-center gap-2 px-4 py-2.5 bg-transparent text-red-400 rounded-lg hover:bg-danger/10 border border-danger font-medium whitespace-nowrap text-sm' title='Excluir os DDRs marcados'><Icon name='trash' className='w-4 h-4' />Excluir<span className='ml-1 px-1.5 py-0.5 rounded-full bg-danger text-white text-[10px] font-semibold'>{selectedCount}</span></button>)}
          {onOpenPrestadoras && <button onClick={onOpenPrestadoras} className='flex items-center gap-2 px-4 py-2.5 bg-slate-700 text-slate-200 rounded-lg hover:bg-slate-600 border border-slate-600 font-medium whitespace-nowrap text-sm' title='Cadastrar/editar prestadoras (Operadora Real)'><Icon name='phone' className='w-4 h-4' />Prestadoras</button>}
          {canEdit && <button onClick={() => onEdit(null)} className='flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium whitespace-nowrap text-sm'><Icon name='plus' className='w-4 h-4' />Novo DDR</button>}
        </>}
        secondRow={<div className='flex flex-wrap gap-3 items-center'>
          {/* Filtro por Status (padrão: Ativos) */}
          <div className='flex items-center gap-2'>
            <span className='text-xs text-slate-500 font-medium'>Status:</span>
            <div className='flex gap-1'>
              {(['Ativos', 'Inativos', 'Todos'] as const).map(st => (
                <button key={st} onClick={() => { setStatusFilter(st); table.setPageIndex(0); }} className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${statusFilter === st ? 'bg-slate-600 text-white font-semibold border border-slate-600' : 'bg-slate-800 text-slate-400 hover:bg-slate-700 border border-slate-700'}`}>
                  {st}
                </button>
              ))}
            </div>
          </div>
          {/* Filtro por Fornecedor */}
          <div className='flex items-center gap-2'>
            <span className='text-xs text-slate-500 font-medium'>Fornecedor:</span>
            <div className='flex gap-1'>
              {['Todos', ...availableCarriers].map(carrier => (
                <button key={carrier} onClick={() => { handleCarrierFilter(carrier); table.setPageIndex(0); }} className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${carrierFilter === carrier ? 'bg-slate-600 text-white font-semibold border border-slate-600' : 'bg-slate-800 text-slate-400 hover:bg-slate-700 border border-slate-700'}`}>
                  {carrier}
                </button>
              ))}
            </div>
          </div>
          {/* Filtro por Operadora Real (hospedagem) */}
          <div className='flex items-center gap-2'>
            <span className='text-xs text-slate-500 font-medium'>Op. Real:</span>
            <select
              value={hostFilter}
              onChange={e => { handleHostFilter(e.target.value); table.setPageIndex(0); }}
              className={`px-3 py-1.5 rounded-lg text-xs ${campo}`}
            >
              <option value='Todos'>Todas</option>
              <option value='Não verificado'>Não verificado</option>
              {availableHostCarriers.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
          </div>
          {/* Toggle: só interconectados (fornecedor ≠ operadora real) */}
          <button
            onClick={() => { setInterconnectedOnly(v => !v); table.setPageIndex(0); }}
            className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors border ${interconnectedOnly ? 'bg-slate-600 text-white font-semibold border-slate-600' : 'bg-slate-800 text-slate-400 hover:bg-slate-700 border-slate-700'}`}
            title='Mostrar apenas DDRs com fornecedor diferente da operadora real'
          >
            ⇄ Só interconectados
          </button>
          {/* Filtro por Estado (UF) */}
          <div className='flex items-center gap-2'>
            <span className='text-xs text-slate-500 font-medium'>UF:</span>
            <select
              value={stateFilter}
              onChange={e => handleStateFilter(e.target.value)}
              className={`px-3 py-1.5 rounded-lg text-xs ${campo}`}
            >
              <option value='Todos'>Todos</option>
              <option value='CNG'>CNG 0800</option>
              {availableStates.map(uf => <option key={uf} value={uf}>{uf}</option>)}
            </select>
          </div>
        </div>}
      />

      {/* Tabela */}
      {primeiraCarga ? <div className='text-center py-12 text-slate-500'>Carregando...</div> : (
        // Sem overflow aqui (21/10/2026): o overflow-hidden que a #556 pôs para arredondar os cantos matava o sticky do
        // cabeçalho (guia §3.6). Medido no HUB com sonda de 3000px: com ele, o cabeçalho rolava para -951px; sem, grudava.
        <CascaDaLista>
            <TabelaDaLista>
              {/* Cabeçalho fixo (CabecalhoDaLista, -top-6: o <main> rola com padding-top de 24px; medido na #470). */}
              <CabecalhoDaLista>
                {table.getHeaderGroups()[0].headers.map(header => (
                  <ColunaDaLista
                    key={header.id}
                    rotulo={flexRender(header.column.columnDef.header, header.getContext())}
                    largura={({ select: 'w-10', id: 'w-14', phoneNumber: 'w-36', city: 'w-40', carrier: 'w-28', hostCarrier: 'w-32', accessAddress: 'w-48', channels: 'w-20', actions: 'w-24' } as Record<string, string>)[header.id] || ''}
                    alinhamento={['channels', 'actions'].includes(header.id) ? 'direita' : ['carrier', 'hostCarrier'].includes(header.id) ? 'centro' : 'esquerda'}
                    ordem={header.column.getCanSort() ? header.column.getIsSorted() : undefined}
                    onOrdenar={() => header.column.toggleSorting()}
                  />
                ))}
              </CabecalhoDaLista>
              <tbody className='divide-y divide-slate-800'>
                {table.getRowModel().rows.length === 0 ? (
                  <tr><td colSpan={visibleColumnCount} className='px-4 py-12 text-center text-slate-500' data-ddrs-vazio={lido ? 'lido' : 'sem-leitura'}>{lido ? 'Nenhum DDR encontrado' : 'Sem leitura dos DDRs'}</td></tr>
                ) : (
                  table.getRowModel().rows.map(row => (
                    // C1: inativo e cancelado ficam apagados, com o nome do estado embaixo do número (a barra está na 1ª célula).
                    <LinhaDaLista key={row.id} data-ddr-status={row.original.status} apagada={!!row.original.status && !['Ativo', 'Portabilidade'].includes(row.original.status)}>
                      {row.getVisibleCells().map(cell => (
                        flexRender(cell.column.columnDef.cell, cell.getContext())
                      ))}
                    </LinhaDaLista>
                  ))
                )}
              </tbody>
            </TabelaDaLista>

          {/* Paginação (PaginacaoDaLista): o recorte, o tamanho com "Todos", Primeira/Última e os números. */}
          <PaginacaoDaLista
            pagina={table.getState().pagination.pageIndex + 1}
            tamanho={table.getState().pagination.pageSize}
            total={filteredRowCount}
            onPagina={(p) => table.setPageIndex(p - 1)}
            onTamanho={(t) => table.setPageSize(t)}
            tamanhos={[50, 100, 200]}
            todos
          />
        </CascaDaLista>
      )}

      {showBulkDelete && (
        // Confirmação de perigo sem nada digitado: fecha ao clicar fora, como já era,
        // e não fecha (nem pelo × novo) enquanto exclui.
        <CascaDeModal
          largura='xs'
          fecharAoClicarFora
          titulo={`Excluir ${selectedCount} DDR(s)`}
          onClose={() => { if (!bulkDeleting) setShowBulkDelete(false); }}
          corpoClassName='p-5 space-y-3'
          rodape={
            <div className='ml-auto flex gap-2'>
              <button onClick={() => setShowBulkDelete(false)} disabled={bulkDeleting} className='px-4 py-2 rounded-lg text-sm font-medium bg-slate-800 text-slate-300 hover:bg-slate-700 disabled:opacity-50'>Cancelar</button>
              <button onClick={applyBulkDelete} disabled={bulkDeleting} className='px-4 py-2 rounded-lg text-sm font-medium bg-red-500 text-white hover:bg-red-600 disabled:opacity-50'>{bulkDeleting ? 'Excluindo...' : `Excluir ${selectedCount}`}</button>
            </div>
          }
        >
              <p className='text-sm text-slate-300'>Os números marcados saem do cadastro. Não há como desfazer.</p>
              <div className='max-h-40 overflow-y-auto rounded-lg border border-central-borda bg-central-elevado/40 p-3'>
                <div className='flex flex-wrap gap-1.5'>
                  {table.getSelectedRowModel().rows.slice(0, 40).map((r: any) => (
                    <span key={r.original.id} className='px-2 py-0.5 rounded bg-slate-700 text-slate-200 text-xs font-mono'>{r.original.phoneNumber}</span>
                  ))}
                  {selectedCount > 40 && <span className='text-slate-400 text-xs self-center'>+{selectedCount - 40}</span>}
                </div>
              </div>
        </CascaDeModal>
      )}

      {showBulk && (() => {
        // A1 + B1 (21/10/2026): os cinco campos à vista; em branco não mexe; apagar é o link "limpar em todos", que deixa
        // o campo vermelho com "desfazer". O rodapé diz em frase o que vai mudar e o botão leva a contagem. Fecha só pelo ×
        // ou Cancelar, travado enquanto aplica (como já era).
        const n = linhasDoEscopo.length;
        const clientesDoEscopo = Array.from(new Set(linhasDoEscopo.map((r: any) => r.original.client?.name || r.original.client?.tradeName || r.original.client?.companyName || 'sem cliente')));
        const trocar = (c: CampoDoLote, valor: string) => setBulkValores((v) => ({ ...v, [c]: valor }));
        const fechar = () => { if (!bulkSaving) { setShowBulk(false); setBulkValores(LOTE_VAZIO); } };
        const ddrs = `${n} DDR${n === 1 ? '' : 's'}`;
        return (
        <CascaDeModal
          largura='sm'
          titulo='Alterar DDRs em lote'
          onClose={fechar}
          corpoClassName='p-4 space-y-3'
          rodape={
            <div className='w-full space-y-3'>
              <p className='text-xs text-slate-400' data-resumo-do-lote>
                {camposAlterados.length === 0
                  ? <>Preencha só o que quer mudar. Campo em branco fica como está.</>
                  : <>Vai mudar <span className='font-semibold text-white'>{ddrs}</span>: {camposAlterados.map(({ campo: c, nome }, k) => (
                      <React.Fragment key={c}>{k > 0 && ' · '}{bulkValores[c] === LIMPAR
                        ? <span className='text-red-400'>{nome} apagado</span>
                        : <>{nome} → <span className='font-semibold text-white'>{valorLegivel(c, bulkValores[c])}</span></>}</React.Fragment>
                    ))}. Os outros campos ficam como estão.</>}
              </p>
              <div className='flex items-center justify-end gap-2'>
                <button onClick={fechar} disabled={bulkSaving} className='px-4 py-2 rounded-lg text-sm font-medium bg-slate-800 text-slate-300 hover:bg-slate-700 disabled:opacity-50'>Cancelar</button>
                <button onClick={applyBulk} disabled={bulkSaving || camposAlterados.length === 0 || n === 0} className='px-4 py-2 rounded-lg text-sm font-medium bg-primary text-white hover:bg-primary-strong disabled:opacity-50 disabled:cursor-not-allowed'>{bulkSaving ? 'Alterando...' : `Alterar ${ddrs}`}</button>
              </div>
            </div>
          }
        >
              <div className='text-sm text-slate-300 bg-central-elevado border border-central-borda rounded-lg px-3 py-2' data-escopo-do-lote>
                {selectedCount > 0
                  ? <><span className='font-semibold text-white'>{n}</span> DDR{n === 1 ? '' : 's'} <span className='text-slate-400'>marcado{n === 1 ? '' : 's'}</span></>
                  : <>Nenhuma linha marcada: <span className='font-semibold text-white'>{n}</span> DDR{n === 1 ? '' : 's'} do <span className='text-slate-400'>filtro atual</span></>}
                {' · '}{clientesDoEscopo.length === 1 ? <>todos de <span className='font-semibold text-white'>{clientesDoEscopo[0]}</span></> : <>de {clientesDoEscopo.length} clientes</>}
              </div>
              {erroPrestadoras && (
                <p role='alert' className={`text-xs ${TONE_TEXT.warning}`} data-prestadoras-nao-lidas>
                  Não consegui ler as prestadoras ({erroPrestadoras}): Fornecedor e Operadora real ficam em "não alterar". Feche e abra de novo para tentar.
                </p>
              )}
              {CAMPOS_DO_LOTE.map(({ campo: c, nome, podeLimpar }) => {
                const v = bulkValores[c];
                const cls = `w-full px-3 py-2 ${campo} rounded-lg text-sm`;
                const ePrestadora = c === 'carrierId' || c === 'hostCarrierId';
                return (
                  <div key={c} data-linha-do-lote={c}>
                    <div className='flex items-baseline justify-between gap-2 mb-1'>
                      <label htmlFor={`lote-${c}`} className='text-xs font-medium text-slate-400'>{nome}</label>
                      {podeLimpar && v !== LIMPAR && (
                        <button type='button' onClick={() => trocar(c, LIMPAR)} className='text-[11px] text-slate-400 hover:text-white underline'>limpar em todos</button>
                      )}
                    </div>
                    {v === LIMPAR ? (
                      <div className='flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-danger text-sm text-red-400' data-limpar-em-todos>
                        <span>Será apagado {n === 1 ? 'no DDR' : `nos ${n} DDRs`}</span>
                        <button type='button' onClick={() => trocar(c, '')} className='text-xs text-red-300 hover:text-white underline'>desfazer</button>
                      </div>
                    ) : ePrestadora ? (
                      <select id={`lote-${c}`} value={v} onChange={(e) => trocar(c, e.target.value)} disabled={!!erroPrestadoras} className={cls}>
                        <option value=''>— não alterar —</option>
                        {prestadoras.map((p: any) => <option key={p.id} value={String(p.id)}>{p.nome}</option>)}
                      </select>
                    ) : c === 'appUrl' && loteComListaDeEndereco ? (
                      <>
                        <select id={`lote-${c}`} value={v} onChange={(e) => trocar(c, e.target.value)} disabled={enderecosComuns.length === 0} className={cls} data-lote-endereco='lista'>
                          {opcaoAtualDispensada('o valor do lote só nasce desta lista ou é "não alterar"; não há valor salvo a mostrar')}
                          <option value=''>— não alterar —</option>
                          {enderecosComuns.map((e) => <option key={e.id} value={`id:${e.id}`}>{e.host}</option>)}
                        </select>
                        {enderecosComuns.length === 0 && (
                          <p className='mt-1 text-[11px] text-slate-500' data-lote-endereco='sem-comum'>
                            Os DDRs do lote são de {listasDoEscopo.length} clientes sem endereço em comum: filtre por um cliente para trocar o endereço.
                          </p>
                        )}
                      </>
                    ) : (
                      <input id={`lote-${c}`} type={c === 'channels' ? 'number' : 'text'} min={c === 'channels' ? 0 : undefined} value={v} onChange={(e) => trocar(c, e.target.value)}
                        placeholder={c === 'appUrl' ? 'não alterar (ex.: sbc.ipcom.app.br)' : c === 'techPrefix' ? 'não alterar (ex.: 511)' : 'não alterar (ex.: 30)'} className={cls} />
                    )}
                  </div>
                );
              })}
        </CascaDeModal>
        );
      })()}
    </div>
  );
};

export default DDRsTable;
